// @refresh reset — provider remounts after HMR; context instance lives in auth-context.ts.
import React, { useContext, useEffect, useState, useCallback } from 'react'
import { authAPI, userAPI, registerAuthTokenSync } from '@/lib/api'
import { getFirebaseAuth, usesSeparateAuthDomain, getConfiguredAuthDomain } from '@/lib/firebase'
import { bridgeFirebaseAuth, signOutFirebaseAuth } from '@/lib/firebaseAuthBridge'
import {
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    FacebookAuthProvider,
} from 'firebase/auth'
import i18n from '@/i18n'
import { storage, clearLegacyUserProfileCache } from '@/utils/storage'
import { clearAadhaarReminderDismiss } from '@/utils/aadhaarReminderStorage'
import {
    clearAuthScopedSessionCaches,
    clearFirebaseAuthSessionKeys,
    isolateSessionCachesForUser,
} from '@/utils/authSessionCache'
import { setMapsViewer } from '@/lib/googleMapsKey'
import { AuthContext, type AuthContextType } from './auth-context'
import type { RegisterUserInput, User } from './auth-types'

export type { User, RegisterUserInput } from './auth-types'

const SOCIAL_AUTH_RETURN_URL_KEY = 'lmi_social_auth_return_url'

const AUTH_ENTRY_PATHS = new Set([
    '/login',
    '/register',
    '/register/google',
    '/register/facebook',
    '/register/phone',
])

function resolveSocialReturnUrl(url: string): string {
    const trimmed = url.trim()
    if (!trimmed || trimmed === '/') return '/dashboard'
    const path = trimmed.split('?')[0].split('#')[0]
    if (AUTH_ENTRY_PATHS.has(path)) return '/dashboard'
    return trimmed
}

/** After social login, avoid hard reload back to /login (causes flicker loops with ProtectedRoute). */
function finishSocialAuthNavigation(returnUrl: string) {
    const target = resolveSocialReturnUrl(returnUrl)
    const current = `${window.location.pathname}${window.location.search}`

    if (target === current) return

    if (AUTH_ENTRY_PATHS.has(window.location.pathname) && target === '/dashboard') {
        return
    }

    window.location.assign(target)
}

function storeSocialReturnUrl(returnUrl?: string) {
    const fallback = `${window.location.pathname}${window.location.search}`
    storage.session.setItem(
        SOCIAL_AUTH_RETURN_URL_KEY,
        resolveSocialReturnUrl(returnUrl ?? fallback),
    )
}

function logSocialAuthContext(label: string) {
    console.info(`[${label}] host=${window.location.hostname} authDomain=${getConfiguredAuthDomain()}`)
}

function getAuthErrorMessage(error: any, defaultMessage: string): string {
    const code = typeof error?.code === 'string' ? error.code : ''
    if (code === 'auth/popup-closed-by-user') {
        return 'Sign-in was cancelled. Please try again.'
    }
    if (code === 'auth/unauthorized-domain') {
        return 'This site is not in Firebase Authorized domains. In Firebase Console → Authentication → Settings, add your domain (e.g. localhost, letmein.ai).'
    }
    if (code === 'auth/operation-not-allowed') {
        return 'Google sign-in is disabled in Firebase. Enable it under Authentication → Sign-in method → Google.'
    }
    if (code === 'auth/internal-error') {
        const separate = typeof window !== 'undefined' && usesSeparateAuthDomain()
        if (separate) {
            return 'Google sign-in failed. With a custom auth domain (auth-stg.letmein.ai), ensure App Check reCAPTCHA allows stg.letmein.ai, OAuth redirect URI is https://auth-stg.letmein.ai/__/auth/handler, and both domains are in Firebase Authorized domains. The app will retry using redirect sign-in.'
        }
        return 'Google sign-in failed (Firebase internal error). On localhost, register an App Check debug token (see browser console). Also verify Google sign-in is enabled and your domain is authorized in Firebase Console.'
    }

    // Axios-style backend error with detail field
    const detail = error?.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) {
        return detail
    }
    // Generic message if present
    if (typeof error?.message === 'string' && error.message.trim()) {
        return error.message
    }
    return defaultMessage
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [authToken, setAuthToken] = useState<string | null>(() => storage.local.getItem('access_token'))
    const [activeRole, setActiveRoleState] = useState<string | null>(() => storage.local.getItem('active_role'))

    useEffect(() => {
        setMapsViewer(user?.id ?? null, user?.google_maps_enabled)
    }, [user?.id, user?.google_maps_enabled])

    const setActiveRole = useCallback((role: string | null) => {
        setActiveRoleState(role);
        if (role) {
            storage.local.setItem('active_role', role);
            // Persistent storage for role recovery (survives logout)
            if (user?.id) {
                storage.local.setItem('id', user.id);
                storage.local.setItem('state', role);
            }
        } else {
            storage.local.removeItem('active_role');
        }
    }, [user?.id]);

    const normalizeInitialRole = useCallback((
        userRole: string,
        roleFromActiveSession: string | null,
        recoveredRole: string | null
    ): string => {
        // Only owner/tenant accounts can intentionally switch between these two dashboard roles.
        const isOwnerTenantUser = userRole === 'owner' || userRole === 'tenant';
        if (!isOwnerTenantUser) {
            return userRole;
        }

        const allowedOwnerTenantRoles = new Set(['owner', 'tenant']);
        if (roleFromActiveSession && allowedOwnerTenantRoles.has(roleFromActiveSession)) {
            return roleFromActiveSession;
        }
        if (recoveredRole && allowedOwnerTenantRoles.has(recoveredRole)) {
            return recoveredRole;
        }
        return userRole;
    }, []);

    // Sync activeRole with user role initially or when user changes
    useEffect(() => {
        if (user) {
            const savedRole = storage.local.getItem('active_role');
            const prevUserId = storage.local.getItem('prev_user_id');
            const lmiId = storage.local.getItem('id');
            const lmiState = storage.local.getItem('state');
            const isNewUser = (storage.local.getItem('prev_user_id') !== user.id) && (lmiId !== user.id);

            // If activeRole is not set or we have a new user session, set initial role
            if (!activeRole || isNewUser || !savedRole) {
                // Only reuse a saved active role for the same user session.
                // For new users, always trust backend user.role first.
                const canReuseSavedRole = prevUserId === user.id;
                const roleFromActiveSession = canReuseSavedRole ? savedRole : null;
                // Recover from persistent state if user matches, otherwise default to user.role.
                const recoveredRole = (lmiId === user.id) ? lmiState : null;
                const initialRole = normalizeInitialRole(user.role, roleFromActiveSession, recoveredRole);
                
                setActiveRole(initialRole);
                storage.local.setItem('prev_user_id', user.id);
            }
        } else {
            storage.local.removeItem('prev_user_id');
        }
    }, [user, activeRole, setActiveRole, normalizeInitialRole])

    useEffect(() => {
        registerAuthTokenSync(setAuthToken)
    }, [])

    const applyAuthSession = useCallback(async (
        access_token: string,
        refresh_token?: string,
        firebase_token?: string,
        options?: { syncRole?: boolean },
    ) => {
        storage.local.setItem('access_token', access_token)
        if (refresh_token) {
            storage.local.setItem('refresh_token', refresh_token)
        }
        setAuthToken(access_token)

        let userData = await authAPI.getCurrentUser()

        if (i18n.language && userData.preferred_language !== i18n.language) {
            try {
                await userAPI.updateProfile({ preferred_language: i18n.language })
                userData = { ...userData, preferred_language: i18n.language }
            } catch (e) {
                console.error('Failed to sync session language to database:', e)
            }
        }

        isolateSessionCachesForUser(userData.id)
        clearFirebaseAuthSessionKeys()
        setUser(userData)
        if (options?.syncRole !== false) {
            setActiveRole(userData.role)
        }

        await bridgeFirebaseAuth(firebase_token ?? userData.firebase_token, userData.id)

        return userData
    }, [setActiveRole])

    const loginWithToken = useCallback(async (
        access_token: string,
        refresh_token?: string,
        firebase_token?: string,
    ) => {
        try {
            await applyAuthSession(access_token, refresh_token, firebase_token, { syncRole: false })
        } catch {
            storage.local.removeItem('access_token')
            storage.local.removeItem('refresh_token')
            setAuthToken(null)
            setUser(null)
            throw new Error('Failed to log in with token.')
        }
    }, [applyAuthSession])

    const completeGoogleSocialAuth = useCallback(async (idToken: string) => {
        const response = await authAPI.googleLogin({ id_token: idToken })

        if (response.needs_registration) {
            storage.session.setItem('firebase_google_id_token', idToken)
            storage.session.setItem('firebase_google_email', response.email || '')
            window.location.href = `/register/google${response.email ? `?email=${encodeURIComponent(response.email)}` : ''}`
            return
        }

        if (response.access_token) {
            await loginWithToken(
                response.access_token,
                response.refresh_token,
                response.firebase_token,
            )
            const returnUrl = storage.session.getItem(SOCIAL_AUTH_RETURN_URL_KEY) || '/dashboard'
            storage.session.removeItem(SOCIAL_AUTH_RETURN_URL_KEY)
            finishSocialAuthNavigation(returnUrl)
            return
        }

        throw new Error('Login failed - no access token received')
    }, [loginWithToken])

    const completeFacebookSocialAuth = useCallback(async (idToken: string) => {
        const response = await authAPI.facebookLogin({ id_token: idToken })

        if (response.needs_registration) {
            storage.session.setItem('firebase_facebook_id_token', idToken)
            storage.session.setItem('firebase_facebook_email', response.email || '')
            window.location.href = `/register/facebook${response.email ? `?email=${encodeURIComponent(response.email)}` : ''}`
            return
        }

        if (response.access_token) {
            await loginWithToken(
                response.access_token,
                response.refresh_token,
                response.firebase_token,
            )
            const returnUrl = storage.session.getItem(SOCIAL_AUTH_RETURN_URL_KEY) || '/dashboard'
            storage.session.removeItem(SOCIAL_AUTH_RETURN_URL_KEY)
            finishSocialAuthNavigation(returnUrl)
            return
        }

        throw new Error('Login failed - no access token received')
    }, [loginWithToken])

    useEffect(() => {
        let cancelled = false

        const initSession = async () => {
            setIsLoading(true)
            try {
                const auth = getFirebaseAuth()
                const redirectResult = await getRedirectResult(auth)

                if (redirectResult?.user && !cancelled) {
                    try {
                        const idToken = await redirectResult.user.getIdToken()
                        const providerId = redirectResult.user.providerData[0]?.providerId ?? ''
                        if (providerId === 'google.com') {
                            await completeGoogleSocialAuth(idToken)
                            return
                        }
                        if (providerId === 'facebook.com') {
                            await completeFacebookSocialAuth(idToken)
                            return
                        }
                    } catch (error) {
                        console.error('[socialAuthRedirect] Error:', error)
                        try {
                            await auth.signOut()
                        } catch {
                            // ignore
                        }
                    }
                }

                if (cancelled) return

                const token = storage.local.getItem('access_token')
                if (token) {
                    setAuthToken(token)
                    try {
                        const userData = await authAPI.getCurrentUser()
                        if (!cancelled) {
                            isolateSessionCachesForUser(userData.id)
                            setUser(userData)
                            await bridgeFirebaseAuth(userData.firebase_token, userData.id)
                        }
                    } catch {
                        if (!cancelled) {
                            clearAuthScopedSessionCaches({ clearAllAssistant: true })
                            storage.local.removeItem('access_token')
                            storage.local.removeItem('refresh_token')
                            clearLegacyUserProfileCache()
                            setAuthToken(null)
                        }
                    }
                } else if (!cancelled) {
                    setAuthToken(null)
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        initSession()
        return () => {
            cancelled = true
        }
    }, [completeGoogleSocialAuth, completeFacebookSocialAuth])

    // Sync language with user preference whenever user data changes
    useEffect(() => {
        if (user?.preferred_language) {
            // Always sync language from user preference
            if (user.preferred_language !== i18n.language) {
                i18n.changeLanguage(user.preferred_language)
            }
        }
    }, [user?.preferred_language])

    const login = async (identifier: string, password: string) => {
        try {
            const response = await authAPI.login({ email: identifier, password })
            await applyAuthSession(
                response.access_token,
                response.refresh_token,
                response.firebase_token,
                { syncRole: false },
            )
        } catch (error: any) {
            throw new Error(getAuthErrorMessage(error, 'Login failed. Please try again.'))
        }
    }


    const register = async (data: RegisterUserInput) => {
        try {
            await authAPI.register({ ...data, preferred_language: i18n.language })
        } catch (error: any) {
            throw new Error(getAuthErrorMessage(error, 'Registration failed. Please try again.'))
        }
    }

    const loginWithGoogle = async (returnUrl?: string) => {
        const auth = getFirebaseAuth()
        try {
            if (auth.currentUser) {
                await auth.signOut()
                await new Promise(resolve => setTimeout(resolve, 100))
            }
            storeSocialReturnUrl(returnUrl)
            const provider = new GoogleAuthProvider()

            if (usesSeparateAuthDomain()) {
                logSocialAuthContext('loginWithGoogle')
                await signInWithRedirect(auth, provider)
                return
            }

            const result = await signInWithPopup(auth, provider)
            const idToken = await result.user.getIdToken()
            await completeGoogleSocialAuth(idToken)
        } catch (error: any) {
            console.error('[loginWithGoogle] Error:', error)
            if (error?.code === 'auth/internal-error') {
                console.error(
                    '[loginWithGoogle] auth/internal-error details:',
                    error?.customData ?? error,
                )
                logSocialAuthContext('loginWithGoogle')
            }
            try {
                await auth.signOut()
            } catch (e) {
                // Ignore sign out errors
            }
            if (error.code === 'auth/popup-closed-by-user') {
                throw new Error('Sign-in was cancelled. Please try again.')
            }
            throw new Error(getAuthErrorMessage(error, 'Google login failed. Please try again.'))
        }
    }

    const loginWithFacebook = async (returnUrl?: string) => {
        const auth = getFirebaseAuth()
        try {
            if (auth.currentUser) {
                await auth.signOut()
                await new Promise(resolve => setTimeout(resolve, 100))
            }
            storeSocialReturnUrl(returnUrl)
            const provider = new FacebookAuthProvider()

            if (usesSeparateAuthDomain()) {
                logSocialAuthContext('loginWithFacebook')
                await signInWithRedirect(auth, provider)
                return
            }

            const result = await signInWithPopup(auth, provider)
            const idToken = await result.user.getIdToken()
            await completeFacebookSocialAuth(idToken)
        } catch (error: any) {
            console.error('[loginWithFacebook] Error:', error)
            try {
                await auth.signOut()
            } catch (e) {
                // Ignore sign out errors
            }
            if (error.code === 'auth/popup-closed-by-user') {
                throw new Error('Sign-in was cancelled. Please try again.')
            }
            throw new Error(getAuthErrorMessage(error, 'Facebook login failed. Please try again.'))
        }
    }


    const googleRegisterFirebase = async (data: Parameters<AuthContextType['googleRegisterFirebase']>[0]) => {
        try {
            const response = await authAPI.googleRegisterFirebase({ ...data, preferred_language: i18n.language })
            if (response.access_token) {
                await loginWithToken(
                    response.access_token,
                    response.refresh_token,
                    response.firebase_token,
                )
            } else {
                throw new Error('Registration failed: No access token received')
            }
        } catch (error: any) {
            throw new Error(getAuthErrorMessage(error, 'Google registration failed. Please try again.'))
        }
    }

    const facebookRegisterFirebase = async (data: Parameters<AuthContextType['facebookRegisterFirebase']>[0]) => {
        try {
            const response = await authAPI.facebookRegisterFirebase({ ...data, preferred_language: i18n.language })
            if (response.access_token) {
                await loginWithToken(
                    response.access_token,
                    response.refresh_token,
                    response.firebase_token,
                )
            } else {
                throw new Error('Registration failed: No access token received')
            }
        } catch (error: any) {
            throw new Error(getAuthErrorMessage(error, 'Facebook registration failed. Please try again.'))
        }
    }

    const logout = useCallback(async () => {
        const userId = user?.id ?? storage.local.getItem('id') ?? undefined

        try {
            await authAPI.logout()
        } catch { }

        await signOutFirebaseAuth()

        if (userId) {
            clearAadhaarReminderDismiss(userId)
        }
        clearAuthScopedSessionCaches({
            previousUserId: userId,
            clearAllAssistant: true,
        })
        storage.local.removeItem('access_token')
        storage.local.removeItem('refresh_token')
        clearLegacyUserProfileCache()
        setUser(null)
        setAuthToken(null)
        setActiveRole(null)
        // Note: We intentionally DO NOT remove 'id' or 'state' here
        // so they can be recovered on the next login of the same user
    }, [setActiveRole, user?.id])

    const refreshUser = useCallback(async () => {
        try {
            const userData = await authAPI.getCurrentUser()
            setUser(userData)
        } catch {
            logout()
        }
    }, [logout])

    const impersonateUser = useCallback(async (userId: string) => {
        const response = await authAPI.startImpersonation(userId)
        await applyAuthSession(response.access_token, response.refresh_token, response.firebase_token)
    }, [applyAuthSession])

    const stopImpersonation = useCallback(async () => {
        const response = await authAPI.stopImpersonation()
        await applyAuthSession(response.access_token, response.refresh_token, response.firebase_token)
    }, [applyAuthSession])

    const changeLanguage = useCallback(async (lang: string) => {
        // 1. Update i18n
        await i18n.changeLanguage(lang)

        // 2. If user is logged in, update database
        if (user) {
            try {
                await userAPI.updateProfile({ preferred_language: lang })
                const updatedUser = { ...user, preferred_language: lang }
                setUser(updatedUser)
            } catch (error) {
                console.error('Failed to update language in database:', error)
            }
        }
    }, [user])

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithToken,
        loginWithGoogle,
        loginWithFacebook,
        register,
        googleRegisterFirebase,
        facebookRegisterFirebase,
        logout,
        refreshUser,
        impersonateUser,
        stopImpersonation,
        authToken,
        activeRole: activeRole || user?.role || null,
        setActiveRole,
        changeLanguage
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
